import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, DataTable, Form, InputGroup, PageHeader } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  PO_TYPE_LABELS,
  PurchaseOrder,
  PurchaseOrderLineItem,
  PurchaseOrderQueryParams,
  purchaseOrderService,
} from '../../../services/purchaseOrderService';
import ConsumptionBar from '../components/ConsumptionBar';
import PoConsumptionDetailModal from '../components/PoConsumptionDetailModal';
import PurchaseOrdersFilters, {
  EMPTY_PO_FILTERS,
  PurchaseOrderFilterState,
} from '../components/PurchaseOrdersFilters';
import { currency, shortDate } from '../../vendors/utils/format';

const StatusBadge: React.FC<{ status: PurchaseOrder['status'] }> = ({ status }) => {
  if (status === 'ACTIVE') return <Badge bg="success">Active</Badge>;
  if (status === 'INACTIVE') return <Badge bg="secondary">Inactive</Badge>;
  return <Badge bg="warning">Reserved</Badge>;
};

/**
 * HE-P01, HE-P02, HE-P03 — the purchase order list that replaces the SharePoint sheet.
 *
 * Read-only apart from the status toggle (HE-P04), which is what the spreadsheet does today.
 * Purchase requisitions and procurement stay in TrueContext this phase.
 */
const PurchaseOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<PurchaseOrderFilterState>(EMPTY_PO_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  /**
   * Expanded rows and their line items. Line items are NOT in the list payload — a PO can carry
   * dozens and the list would pay for them on every page load — so they are fetched on first
   * expand and kept. Collapsing keeps the cache: re-opening the same row must not refetch.
   */
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [lineItems, setLineItems] = useState<Map<number, PurchaseOrderLineItem[]>>(new Map());
  const [loadingLines, setLoadingLines] = useState<Set<number>>(new Set());
  const [consumptionPo, setConsumptionPo] = useState<PurchaseOrder | null>(null);

  const toggleExpanded = useCallback(
    async (po: PurchaseOrder) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(po.id)) next.delete(po.id);
        else next.add(po.id);
        return next;
      });

      if (lineItems.has(po.id) || loadingLines.has(po.id)) return;

      setLoadingLines((prev) => new Set(prev).add(po.id));
      try {
        const items = await purchaseOrderService.getLineItems(po.id);
        setLineItems((prev) => new Map(prev).set(po.id, items));
      } catch {
        // Cache the empty result rather than retrying on every toggle; the row says so.
        setLineItems((prev) => new Map(prev).set(po.id, []));
      } finally {
        setLoadingLines((prev) => {
          const next = new Set(prev);
          next.delete(po.id);
          return next;
        });
      }
    },
    [lineItems, loadingLines]
  );

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
  });

  const fetchPurchaseOrders = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        setError(null);

        const params: PurchaseOrderQueryParams = {
          page,
          limit: pagination.limit,
          ...(searchTerm && { search: searchTerm }),
          ...(filters.vendorIds.length && { vendorIds: filters.vendorIds }),
          ...(filters.types.length && { types: filters.types as PurchaseOrderQueryParams['types'] }),
          ...(filters.serviceAreaIds.length && { serviceAreaIds: filters.serviceAreaIds }),
          ...(filters.foremanAreaIds.length && { foremanAreaIds: filters.foremanAreaIds }),
          ...(filters.statuses.length && {
            statuses: filters.statuses as PurchaseOrderQueryParams['statuses'],
          }),
          ...(sortBy && { sortBy, sortOrder: sortDir }),
        };

        const response = await purchaseOrderService.getPurchaseOrdersPaginated(params);
        setPurchaseOrders(response.data);
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.page,
          totalCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
        }));
      } catch (err) {
        console.error('Error loading purchase orders:', err);
        setError(err instanceof Error ? err.message : 'Failed to load purchase orders');
        setPurchaseOrders([]);
        setPagination((prev) => ({ ...prev, totalCount: 0, totalPages: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, searchTerm, filters, sortBy, sortDir]
  );

  // Filter, search and sort changes all reset to page 1: staying on page 7 of a result set
  // that now has two pages shows an empty table and reads as "no results".
  useEffect(() => {
    fetchPurchaseOrders(1);
  }, [searchTerm, filters, sortBy, sortDir, pagination.limit]);

  const hasFilters =
    !!searchTerm ||
    !!filters.vendorIds.length ||
    !!filters.types.length ||
    !!filters.serviceAreaIds.length ||
    !!filters.foremanAreaIds.length ||
    !!filters.statuses.length;

  const toggleSearch = () => {
    if (isSearchVisible) {
      setSearchTerm('');
      setSearchInput('');
      setIsSearchVisible(false);
      return;
    }
    // The revealed input carries autoFocus, so the caret lands there without a second click.
    setIsSearchVisible(true);
  };

  const columns: DataTableColumn<PurchaseOrder>[] = [
    {
      key: 'expander',
      header: '',
      className: 'tw-w-8',
      cell: (po) => (
        <button
          type="button"
          aria-expanded={expanded.has(po.id)}
          aria-label={expanded.has(po.id) ? `Hide line items for ${po.poNumber}` : `Show line items for ${po.poNumber}`}
          className="tw-border-0 tw-bg-transparent tw-p-1 tw-text-content-muted hover:tw-text-content"
          onClick={(e) => {
            // The row itself navigates to the detail page; expanding must not.
            e.stopPropagation();
            void toggleExpanded(po);
          }}
        >
          <i className={`bi ${expanded.has(po.id) ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
        </button>
      ),
    },
    {
      key: 'poNumber',
      header: 'PO Number',
      sortable: true,
      mobile: 'title',
      cell: (po) => (
        <button
          type="button"
          className="tw-text-primary tw-underline tw-bg-transparent tw-border-0 tw-p-0"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/purchase-orders/${po.id}`);
          }}
        >
          {po.poNumber}
        </button>
      ),
    },
    {
      /*
       * A purchase order whose work-instructions review was deferred still owes something, and
       * that is invisible on a list of numbers and amounts. It gets its own narrow column
       * rather than a suffix on the PO number so it survives the mobile card layout, where a
       * badge appended to the title would be truncated.
       *
       * Deliberately silent for "Yes" and "Not Applicable": a warning shown on every row is a
       * warning nobody reads.
       */
      key: 'workInstructions',
      header: '',
      className: 'tw-w-8',
      // Server-computed from the per-line answers, so the icon and the feed agree.
      cell: (po) =>
        po.workInstructionsOutstanding ? (
          <i
            className="bi bi-exclamation-triangle-fill tw-text-warning"
            title="At least one line still needs its work instructions reviewed"
            aria-label="Work instructions review outstanding on at least one line"
          />
        ) : null,
    },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      mobile: 'subtitle',
      cell: (po) => po.description || '—',
    },
    {
      key: 'vendor',
      header: 'Vendor',
      sortable: true,
      cell: (po) => po.vendor?.name ?? '—',
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      cell: (po) => PO_TYPE_LABELS[po.type] ?? po.type,
    },
    {
      key: 'serviceArea',
      header: 'Service Area',
      sortable: true,
      cell: (po) => po.serviceArea?.name ?? '—',
    },
    {
      key: 'foremanArea',
      header: 'Foreman Area',
      sortable: true,
      cell: (po) => po.foremanArea?.name ?? '—',
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      align: 'right',
      cell: (po) => currency(po.amount),
    },
    {
      key: 'progress',
      header: 'Progress',
      // Not sortable: consumption is computed per page rather than stored, so the server has
      // nothing to order by. Offering the affordance would sort only the visible rows.
      mobile: 'meta',
      cell: (po) => (
        <ConsumptionBar
          consumption={po.consumption}
          compact
          onClick={() => setConsumptionPo(po)}
        />
      ),
    },
    {
      key: 'requestedByName',
      header: 'Requested By',
      sortable: true,
      cell: (po) =>
        po.requestedBy ? `${po.requestedBy.name} ${po.requestedBy.lastName}` : po.requestedByName || '—',
    },
    {
      key: 'requestDate',
      header: 'Requested',
      sortable: true,
      cell: (po) => shortDate(po.requestDate),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      cell: (po) => <StatusBadge status={po.status} />,
    },
  ];

  return (
    <div className="purchase-orders-page tw-p-4">
      <PageHeader
        title="Purchase Orders"
        actions={[
          {
            key: 'search',
            label: `Search${searchTerm ? ` (${searchTerm.length > 20 ? `${searchTerm.substring(0, 20)}…` : searchTerm})` : ''}`,
            icon: isSearchVisible ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: isSearchVisible || !!searchTerm,
            title: isSearchVisible ? 'Close search' : 'Search purchase orders',
            onClick: toggleSearch,
          },
        ]}
      />

      {isSearchVisible && (
        <Card className="tw-mb-3">
          <Card.Body>
            <InputGroup>
              <Form.Control
                // The input only mounts when the panel opens, so autoFocus fires exactly
                // once per open. Form.Control does not forward refs, so this is the way to
                // land the caret without a second click (matching /work-ids).
                autoFocus
                type="text"
                placeholder="Search by PO number, description, vendor, or reference…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && setSearchTerm(searchInput.trim())}
              />
              <Button variant="primary" type="button" onClick={() => setSearchTerm(searchInput.trim())}>
                <i className="bi bi-search tw-mr-2" />
                Search
              </Button>
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSearchTerm('');
                  }}
                >
                  Clear
                </Button>
              )}
            </InputGroup>
          </Card.Body>
        </Card>
      )}

      <PurchaseOrdersFilters
        filters={filters}
        onFilterChange={setFilters}
        onClearFilters={() => setFilters(EMPTY_PO_FILTERS)}
      />

      {error && (
        <div className="tw-mb-3 tw-text-danger" role="alert">
          {error}
        </div>
      )}

      <DataTable
        columns={columns}
        data={purchaseOrders}
        rowKey={(po) => po.id}
        loading={loading}
        framed
        hover
        onRowClick={(po) => navigate(`/purchase-orders/${po.id}`)}
        isExpanded={(po) => expanded.has(po.id)}
        renderExpanded={(po) => {
          if (loadingLines.has(po.id)) {
            return <div className="tw-p-3 tw-text-sm tw-text-content-muted">Loading line items…</div>;
          }
          const items = lineItems.get(po.id) ?? [];
          if (items.length === 0) {
            return <div className="tw-p-3 tw-text-sm tw-text-content-muted">No line items on this purchase order.</div>;
          }
          return (
            <div className="tw-overflow-x-auto tw-p-3">
              <table className="tw-w-full tw-text-sm">
                <thead>
                  <tr className="tw-text-left tw-text-xs tw-uppercase tw-tracking-wide tw-text-content-muted">
                    <th className="tw-pb-1 tw-pr-4 tw-font-medium">Resource</th>
                    <th className="tw-pb-1 tw-pr-4 tw-font-medium">Task</th>
                    <th className="tw-pb-1 tw-pr-4 tw-font-medium tw-text-right">Quantity</th>
                    <th className="tw-pb-1 tw-font-medium">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((li) => (
                    <tr key={li.id} className="tw-border-t tw-border-line">
                      <td className="tw-py-1 tw-pr-4">{li.resource?.name ?? li.description ?? '—'}</td>
                      <td className="tw-py-1 tw-pr-4">
                        {li.task ? li.task.taskNumber ?? String(li.task.id) : '—'}
                      </td>
                      <td className="tw-py-1 tw-pr-4 tw-text-right tw-tabular-nums">{li.quantity}</td>
                      <td className="tw-py-1">{li.unit ?? li.resource?.unit ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={(key, dir) => {
          setSortBy(key);
          setSortDir(dir);
        }}
        emptyMessage={
          <div className="tw-py-2">
            <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2" />
            {/*
              "match these filters" is only true when something IS filtered. With none applied
              it reads as though the user mis-filtered, when the table is simply empty.
            */}
            {hasFilters
              ? 'No purchase orders match these filters.'
              : 'No purchase orders yet. They arrive from the TrueContext import.'}
          </div>
        }
      />

      {pagination.totalPages > 1 && (
        <div className="tw-mt-3">
          <ServerPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.limit}
            onPageChange={(page) => fetchPurchaseOrders(page)}
            onPageSizeChange={(size) => setPagination((prev) => ({ ...prev, limit: size, page: 1 }))}
            loading={loading}
          />
        </div>
      )}

      <PoConsumptionDetailModal
        show={!!consumptionPo}
        po={consumptionPo}
        onHide={() => setConsumptionPo(null)}
      />
    </div>
  );
};

export default PurchaseOrdersPage;
